import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  FormControl, FormErrorMessage,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput, NumberInputField, NumberInputStepper,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tooltip,
  Tr,
  useToast
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState, useCallback, memo } from "react";
import { useTranslation } from 'react-i18next';
import { Error, WorkingDaysType } from "../../contexts/types";
import { getFiscalYearsString, isSameFiscalYear } from "../../utils/calculation";
import { isPropsEqual } from "../../utils/utils";

/** 月の日数（1月～12月） */
const MONTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

const WD = 'features.InvestmentScreening.WorkingDays';

/** 4月～3月順に並び替えた月の日数（年度表示用） */
const FISCAL_YEAR_MONTHS = [...MONTHS.slice(3, 12), ...MONTHS.slice(0, 3)] as const;

/**
 * エラー情報を初期化する。
 * @returns
 */
const createEmptyErrors = (): Error[] => {
  return Array(12).fill(null).map(() => ({
    isError: false,
    errorMessage: "",
  }));
};

/**
 * メモ化された数値入力セルのProps
 */
interface MemoizedNumberCellProps {
  value: number;
  maxValue: number;
  isInvalid: boolean;
  shiftedIndex: number;
  onChange: (shiftedIndex: number, value: number) => void;
  onBlur: () => void;
}

/**
 * メモ化された数値入力セル
 */
const MemoizedNumberCell = memo(({
  value,
  maxValue,
  isInvalid,
  shiftedIndex,
  onChange,
  onBlur,
}: MemoizedNumberCellProps) => {
  return (
    <Td padding={1}>
      <FormControl isInvalid={isInvalid}>
        <NumberInput
          w="70px"
          min={0}
          max={maxValue}
          precision={0}
          step={1}
          clampValueOnBlur={true}
          value={value}
          onChange={(_, valueAsNumber) => onChange(shiftedIndex, valueAsNumber)}
          onBlur={onBlur}
        >
          <NumberInputField textAlign="right" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </FormControl>
    </Td>
  );
}, (prevProps, nextProps) => {
  // 値、エラー状態、または最大値が変わった場合のみ再レンダリング
  return prevProps.value === nextProps.value &&
    prevProps.isInvalid === nextProps.isInvalid &&
    prevProps.maxValue === nextProps.maxValue;
});

/**
 * メモ化されたライン数入力セルのProps
 */
interface MemoizedLineCountCellProps {
  value: number;
  shiftedIndex: number;
  onChange: (shiftedIndex: number, value: number) => void;
  onBlur?: () => void;
}

/**
 * ライン数を正規化する（0〜99、小数第2位まで）
 * ※他製品との共用ラインを割合で表すため小数を許可（例：1.5ライン）
 */
const normalizeLineCount = (value: number): number => {
  if (isNaN(value) || value < 0) return 0;
  if (value > 99) return 99;
  return Math.round(value * 100) / 100;
};

/**
 * メモ化されたライン数入力セル
 */
const MemoizedLineCountCell = memo(({
  value,
  shiftedIndex,
  onChange,
  onBlur,
}: MemoizedLineCountCellProps) => {
  // 入力途中の文字列（"1." 等）を保持するため、表示値は文字列で管理する
  const [inputValue, setInputValue] = useState<string>(String(value));

  // 外部から値が変わった場合のみ同期する（入力途中の文字列と数値が一致する場合は維持）
  useEffect(() => {
    setInputValue(prev => (Number(prev) === value ? prev : String(value)));
  }, [value]);

  return (
    <Td padding={1}>
      <NumberInput
        w="70px"
        min={0}
        max={99}
        step={0.5}
        clampValueOnBlur={true}
        value={inputValue}
        onChange={(valueAsString, valueAsNumber) => {
          setInputValue(valueAsString);
          onChange(shiftedIndex, valueAsNumber);
        }}
        onBlur={() => {
          // 確定時は正規化後の値で表示を揃える（空欄→0、"1."→1 等）
          setInputValue(String(value));
          onBlur?.();
        }}
      >
        {/* 小数（最大 "99.99"）が見切れないよう左右の余白を詰める（右はステッパー幅24px＋2px） */}
        <NumberInputField textAlign="right" ps={1} pe="26px" />
        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      </NumberInput>
    </Td>
  );
}, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value;
});

/**
 * 年度別稼働日数コンポーネントのパラメータ
 */
interface FiscalYearWorkingDaysProps {
  fiscalYear: string;
  fiscalYearLabel: string;
  workingDaysData: WorkingDaysType;
  showWorkOnHoliday: boolean;
  onWorkingDaysChange: (fiscalYear: string, propertyName: 'factoryWorkingDays' | 'workingDays' | 'workOnWeekendAndHolidays' | 'workOnHolidays', index: number, value: number) => void;
  onLineCountChange: (fiscalYear: string, index: number, value: number) => void;
  onPropertyChange: (fiscalYear: string, propertyName: string, value: any) => void;
  onBlur: () => void;
}

/**
 * 年度別稼働日数コンポーネント（メモ化）
 */
const FiscalYearWorkingDays = memo(({
  fiscalYear,
  fiscalYearLabel,
  workingDaysData,
  showWorkOnHoliday,
  onWorkingDaysChange,
  onLineCountChange,
  onPropertyChange,
  onBlur,
}: FiscalYearWorkingDaysProps) => {
  const { t } = useTranslation();
  /** エラー情報 */
  const [errors, setErrors] = useState<Error[]>(createEmptyErrors());

  /**
   * エラー情報を変更する。
   */
  const changeErrors = useCallback((index: number, isError: boolean, errorMessage: string) => {
    setErrors((prevState) => {
      if (!prevState || index >= prevState.length) {
        return prevState;
      }
      const updatedArray = [...prevState];
      updatedArray[index] = { isError, errorMessage };
      return updatedArray;
    });
  }, []);

  /**
   * 稼働日数バリデーション
   */
  const validWorkingDay = useCallback((index: number, workingDay: number, weekendWorkDay: number): boolean => {
    const sumDays = workingDay + weekendWorkDay;
    if (MONTHS[index] < sumDays) {
      changeErrors(index, true, t(`${WD}.validation.maxDays`, { days: MONTHS[index] }));
      return false;
    }
    return true;
  }, [changeErrors]);

  /**
   * 祝日出勤日数バリデーション
   */
  const validHolidayWorkDays = useCallback((index: number, weekendWorkDay: number, holidayWorkDays: number): boolean => {
    if (weekendWorkDay < holidayWorkDays) {
      changeErrors(index, true, t(`${WD}.validation.holidayWorkMax`, { days: weekendWorkDay }));
      return false;
    }
    return true;
  }, [changeErrors]);

  /**
   * 工場稼働日数を4月～3月順に変換
   */
  const factoryWorkingDaysDisplay = useMemo(() => {
    const factoryWorkingDays = workingDaysData.factoryWorkingDays || Array(12).fill(0);
    return [...factoryWorkingDays.slice(3, 12), ...factoryWorkingDays.slice(0, 3)];
  }, [workingDaysData.factoryWorkingDays]);

  /**
   * 稼働日数を4月～3月順に変換
   */
  const workingDaysDisplay = useMemo(() => [
    ...workingDaysData.workingDays.slice(3, 12),
    ...workingDaysData.workingDays.slice(0, 3),
  ], [workingDaysData.workingDays]);

  const weekendWorkDaysDisplay = useMemo(() => [
    ...workingDaysData.workOnWeekendAndHolidays.slice(3, 12),
    ...workingDaysData.workOnWeekendAndHolidays.slice(0, 3),
  ], [workingDaysData.workOnWeekendAndHolidays]);

  const holidayWorkDaysDisplay = useMemo(() => [
    ...workingDaysData.workOnHolidays.slice(3, 12),
    ...workingDaysData.workOnHolidays.slice(0, 3),
  ], [workingDaysData.workOnHolidays]);

  const lineCountDisplay = useMemo(() => {
    const lineCount = workingDaysData.lineCount || Array(12).fill(1);
    return [...lineCount.slice(3, 12), ...lineCount.slice(0, 3)];
  }, [workingDaysData.lineCount]);

  /**
   * 工場稼働日数入力ハンドラ
   */
  const handleFactoryWorkingDaysChange = useCallback((shiftedIndex: number, valueAsNumber: number) => {
    if (isNaN(valueAsNumber)) valueAsNumber = 0;
    else if (valueAsNumber < 0) valueAsNumber = 0;
    else if (FISCAL_YEAR_MONTHS[shiftedIndex] < valueAsNumber) valueAsNumber = FISCAL_YEAR_MONTHS[shiftedIndex];

    setErrors(createEmptyErrors());

    const originalIndex = (shiftedIndex + 3) % 12;

    // 工場稼働日数 < 現在の稼働日数の場合はエラー
    const currentWorkingDay = workingDaysData.workingDays[originalIndex] ?? 0;
    if (valueAsNumber < currentWorkingDay) {
      changeErrors(originalIndex, true, t(`${WD}.validation.factoryWorkingDaysMin`, { days: currentWorkingDay }));
      return;
    }

    onWorkingDaysChange(fiscalYear, "factoryWorkingDays", originalIndex, valueAsNumber);
  }, [fiscalYear, onWorkingDaysChange, changeErrors, workingDaysData.workingDays]);

  /**
   * 稼働日数入力ハンドラ
   */
  const handleWorkingDaysChange = useCallback((shiftedIndex: number, valueAsNumber: number) => {
    if (isNaN(valueAsNumber)) valueAsNumber = 0;
    else if (valueAsNumber < 0) valueAsNumber = 0;
    else if (FISCAL_YEAR_MONTHS[shiftedIndex] < valueAsNumber) valueAsNumber = FISCAL_YEAR_MONTHS[shiftedIndex];

    setErrors(createEmptyErrors());

    const originalIndex = (shiftedIndex + 3) % 12;
    if (!validWorkingDay(originalIndex, valueAsNumber, workingDaysData.workOnWeekendAndHolidays[originalIndex])) {
      return;
    }

    // 稼働日数 > 工場稼働日数の場合はエラー
    const factoryWorkingDays = workingDaysData.factoryWorkingDays || Array(12).fill(0);
    const currentFactoryWorkingDay = factoryWorkingDays[originalIndex] ?? 0;
    if (currentFactoryWorkingDay > 0 && valueAsNumber > currentFactoryWorkingDay) {
      changeErrors(originalIndex, true, t(`${WD}.validation.workingDaysMax`, { days: currentFactoryWorkingDay }));
      return;
    }

    onWorkingDaysChange(fiscalYear, "workingDays", originalIndex, valueAsNumber);
  }, [fiscalYear, onWorkingDaysChange, validWorkingDay, changeErrors, workingDaysData.workOnWeekendAndHolidays, workingDaysData.factoryWorkingDays]);

  /**
   * 休日出勤入力ハンドラ
   */
  const handleWeekendWorkChange = useCallback((shiftedIndex: number, valueAsNumber: number) => {
    if (isNaN(valueAsNumber)) valueAsNumber = 0;
    else if (valueAsNumber < 0) valueAsNumber = 0;
    else if (FISCAL_YEAR_MONTHS[shiftedIndex] < valueAsNumber) valueAsNumber = FISCAL_YEAR_MONTHS[shiftedIndex];

    setErrors(createEmptyErrors());

    const originalIndex = (shiftedIndex + 3) % 12;
    if (!validWorkingDay(originalIndex, workingDaysData.workingDays[originalIndex], valueAsNumber)) {
      return;
    }

    const currentHolidayWorkDay = workingDaysData.workOnHolidays[originalIndex] ?? 0;
    if (valueAsNumber < currentHolidayWorkDay) {
      changeErrors(originalIndex, true, t(`${WD}.validation.holidayWorkMin`, { days: currentHolidayWorkDay }));
      return;
    }

    onWorkingDaysChange(fiscalYear, "workOnWeekendAndHolidays", originalIndex, valueAsNumber);
  }, [fiscalYear, onWorkingDaysChange, validWorkingDay, changeErrors, workingDaysData.workingDays, workingDaysData.workOnHolidays]);

  /**
   * 祝日出勤入力ハンドラ
   */
  const handleHolidayWorkChange = useCallback((shiftedIndex: number, valueAsNumber: number) => {
    if (isNaN(valueAsNumber)) valueAsNumber = 0;
    else if (valueAsNumber < 0) valueAsNumber = 0;
    else if (FISCAL_YEAR_MONTHS[shiftedIndex] < valueAsNumber) valueAsNumber = FISCAL_YEAR_MONTHS[shiftedIndex];

    setErrors(createEmptyErrors());

    const originalIndex = (shiftedIndex + 3) % 12;
    if (!validHolidayWorkDays(originalIndex, workingDaysData.workOnWeekendAndHolidays[originalIndex], valueAsNumber)) {
      return;
    }

    onWorkingDaysChange(fiscalYear, "workOnHolidays", originalIndex, valueAsNumber);
  }, [fiscalYear, onWorkingDaysChange, validHolidayWorkDays, workingDaysData.workOnWeekendAndHolidays]);

  /**
   * ライン数入力ハンドラ
   */
  const handleLineCountChange = useCallback((shiftedIndex: number, valueAsNumber: number) => {
    const originalIndex = (shiftedIndex + 3) % 12;
    onLineCountChange(fiscalYear, originalIndex, normalizeLineCount(valueAsNumber));
  }, [fiscalYear, onLineCountChange]);

  return (
    <Accordion allowToggle>
      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box flex="1" textAlign="left">
              {fiscalYearLabel}
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <Table>
            <Thead>
              <Tr>
                <Td></Td>
                {FISCAL_YEAR_MONTHS.map((days, index) => (
                  <Td key={index} padding={1} textAlign="center" fontSize={16}>
                    <Tooltip label={t(`${WD}.tooltip.daysInMonth`, { days })}>
                      {t('common.format.month', { month: ((index + 4 - 1) % 12) + 1 })}
                    </Tooltip>
                  </Td>
                ))}
                <Td whiteSpace="nowrap" textAlign="center" fontSize={16}>
                  {t(`${WD}.yearlyTotal`)}
                </Td>
              </Tr>
            </Thead>

            <Tbody>
              {/* 工場稼働日数 */}
              <Tr>
                <Td>
                  <Tooltip placement="top" hasArrow label={t(`${WD}.tooltip.calendarWorkingDays`)}>
                    {t(`${WD}.factoryWorkingDays`)}
                  </Tooltip>
                </Td>
                {factoryWorkingDaysDisplay.map((factoryWorkingDay, shiftedIndex) => (
                  <MemoizedNumberCell
                    key={shiftedIndex}
                    value={factoryWorkingDay}
                    maxValue={FISCAL_YEAR_MONTHS[shiftedIndex]}
                    isInvalid={errors[(shiftedIndex + 3) % 12].isError}
                    shiftedIndex={shiftedIndex}
                    onChange={handleFactoryWorkingDaysChange}
                    onBlur={onBlur}
                  />
                ))}
                <Td minW={100} textAlign="right">
                  <Text>{workingDaysData.sumFactoryWorkingDays || 0} {t('common.label.days')}</Text>
                </Td>
              </Tr>

              {/* (内)稼働日数 */}
              <Tr>
                <Td>
                  {t(`${WD}.innerWorkingDays`)}
                </Td>
                {workingDaysDisplay.map((workingDay, shiftedIndex) => (
                  <MemoizedNumberCell
                    key={shiftedIndex}
                    value={workingDay}
                    maxValue={FISCAL_YEAR_MONTHS[shiftedIndex]}
                    isInvalid={errors[(shiftedIndex + 3) % 12].isError}
                    shiftedIndex={shiftedIndex}
                    onChange={handleWorkingDaysChange}
                    onBlur={onBlur}
                  />
                ))}
                <Td minW={100} textAlign="right">
                  <Text>{workingDaysData.sumWorkingDays} {t('common.label.days')}</Text>
                </Td>
              </Tr>

              {showWorkOnHoliday && (
                <>
                  {/* 休日稼働日数 */}
                  <Tr>
                    <Td>
                      {t(`${WD}.holidayWorkDays`)}
                    </Td>
                    {weekendWorkDaysDisplay.map((weekendWork, shiftedIndex) => (
                      <MemoizedNumberCell
                        key={shiftedIndex}
                        value={weekendWork}
                        maxValue={FISCAL_YEAR_MONTHS[shiftedIndex]}
                        isInvalid={errors[(shiftedIndex + 3) % 12].isError}
                        shiftedIndex={shiftedIndex}
                        onChange={handleWeekendWorkChange}
                        onBlur={onBlur}
                      />
                    ))}
                    <Td textAlign="right">
                      <Text>{workingDaysData.sumWeekendWorkDays} {t('common.label.days')}</Text>
                    </Td>
                  </Tr>

                  {/* (内)祝日出勤 */}
                  <Tr>
                    <Td>
                      <Tooltip placement="top" hasArrow label={<Box whiteSpace="pre-wrap">{t(`${WD}.tooltip.holidayWorkNote`)}</Box>}>
                        {t(`${WD}.holidayWork`)}
                      </Tooltip>
                    </Td>
                    {holidayWorkDaysDisplay.map((holidayWork, shiftedIndex) => (
                      <MemoizedNumberCell
                        key={shiftedIndex}
                        value={holidayWork}
                        maxValue={FISCAL_YEAR_MONTHS[shiftedIndex]}
                        isInvalid={errors[(shiftedIndex + 3) % 12].isError}
                        shiftedIndex={shiftedIndex}
                        onChange={handleHolidayWorkChange}
                        onBlur={onBlur}
                      />
                    ))}
                    <Td textAlign="right">
                      <Text>{workingDaysData.sumHolidayWorkDays} {t('common.label.days')}</Text>
                    </Td>
                  </Tr>
                </>
              )}

              {/* 稼働ライン数 */}
              <Tr>
                <Td>
                  <Tooltip placement="top" hasArrow label={<Box whiteSpace="pre-wrap">{t(`${WD}.tooltip.activeLinesNote`)}</Box>}>
                    {t(`${WD}.activeLines`)}
                  </Tooltip>
                </Td>
                {lineCountDisplay.map((lineCount, shiftedIndex) => (
                  <MemoizedLineCountCell
                    key={shiftedIndex}
                    value={lineCount}
                    shiftedIndex={shiftedIndex}
                    onChange={handleLineCountChange}
                    onBlur={onBlur}
                  />
                ))}
                <Td textAlign="right">
                  <Text></Text>
                </Td>
              </Tr>

              <Tr>
                <Td colSpan={13} textAlign="right">
                  <Text w="fit-content" ml="auto">{t(`${WD}.totalActualWorkingDays`)}</Text>
                </Td>
                <Td textAlign="right">
                  <Text>{
                    workingDaysData.sumWorkingDays
                    + (showWorkOnHoliday ? workingDaysData.sumWeekendWorkDays || 0 : 0)
                  } {t('common.label.days')}</Text>
                </Td>
              </Tr>
            </Tbody>
          </Table>

          {/* エラーメッセージ */}
          <FormControl isInvalid={errors.some(e => e.isError)}>
            <FormErrorMessage>{errors.find(e => e.isError)?.errorMessage}</FormErrorMessage>
          </FormControl>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
});

/**
 * パラメータ
 */
interface Props {
  showWorkOnHoliday: boolean;
  workingDays: { fiscalYear: string, workingDaysData: WorkingDaysType }[],
  projectStartDate: string,
  setWorkingDays: React.Dispatch<React.SetStateAction<{ fiscalYear: string, workingDaysData: WorkingDaysType }[]>>,
  setIsValidWorkingDays: React.Dispatch<React.SetStateAction<boolean>>,
  maxHeight: string,
}

/**
 * 稼働日数
 * @param props パラメータ
 * @returns
 */
const WorkingDays: React.FC<Props> = memo((props: Props) => {
  const { t } = useTranslation();
  /** タイトル */
  const TITLE = t(`${WD}.title`);

  // console.info(`★レンダリング - 稼働日数`); // 再描画確認用

  /** トースト表示用 */
  const toast = useToast();

  /** 年度文字列配列（画面表示用） */
  const fiscalYearsString = useMemo(() => {
    const totalFiscalYears = props.workingDays.length;
    return getFiscalYearsString(props.projectStartDate || dayjs().format("YYYY-MM-DD"), totalFiscalYears);
  }, [props.projectStartDate, props.workingDays.length]);

  /** 【一括入力】 開始年度 */
  const [bulkStartFiscalYear, setBulkStartFiscalYear] = useState("");

  /** 【一括入力】 終了年度 */
  const [bulkEndFiscalYear, setBulkEndFiscalYear] = useState("");

  /** 【一括入力】 月別工場稼働日数（4月～3月の順） */
  const [bulkMonthlyFactoryWorkingDays, setBulkMonthlyFactoryWorkingDays] = useState<number[]>(Array(12).fill(0));

  /** 【一括入力】 月別稼働日数（4月～3月の順） */
  const [bulkMonthlyWorkingDays, setBulkMonthlyWorkingDays] = useState<number[]>(Array(12).fill(0));

  /** 【一括入力】 月別休日出勤（4月～3月の順） */
  const [bulkMonthlyWeekendWorkDays, setBulkMonthlyWeekendWorkDays] = useState<number[]>(Array(12).fill(0));

  /** 【一括入力】 月別祝日出勤（4月～3月の順） */
  const [bulkMonthlyHolidayWorkDays, setBulkMonthlyHolidayWorkDays] = useState<number[]>(Array(12).fill(0));

  /** 【一括入力】 月別ライン数（4月～3月の順） */
  const [bulkMonthlyLineCount, setBulkMonthlyLineCount] = useState<number[]>(Array(12).fill(1));



  /**
   * 一括入力の開始年度および終了年度の初期化
   */
  useEffect(() => {
    if (fiscalYearsString?.length > 0) {
      const firstFiscalYear = fiscalYearsString[0];
      const lastFiscalYear = fiscalYearsString[fiscalYearsString.length - 1];

      // 開始年度・終了年度が空の場合のみ初期化（ユーザーの手動変更を保持）
      if (!bulkStartFiscalYear) {
        setBulkStartFiscalYear(firstFiscalYear);
      }

      if (!bulkEndFiscalYear) {
        setBulkEndFiscalYear(lastFiscalYear);
      }
    }
  }, [fiscalYearsString]); // fiscalYearsStringが変更された時のみ実行

  /**
   * 稼働日数情報設定（初回のみ）
   */
  useEffect(() => {
    // 初期値生成関数
    const createInitialWorkingDaysData = (): WorkingDaysType => {
      return {
        factoryWorkingDays: Array(12).fill(0),
        sumFactoryWorkingDays: 0,
        workingDays: Array(12).fill(0),
        sumWorkingDays: 0,
        workOnWeekendAndHolidays: Array(12).fill(0),
        sumWeekendWorkDays: 0,
        workOnHolidays: Array(12).fill(0),
        sumHolidayWorkDays: 0,
        lineCount: Array(12).fill(1),
        requiredOverTimes: Array(12).fill(false),
      };
    };

    // 不足分を充填する関数（数値）
    const padNumberArray = (arr?: number[]): number[] => {
      if (!arr) arr = [];
      return arr.length < 12 ? [...arr, ...Array(12 - arr.length).fill(0)] : arr;
    };

    // 不足分を充填する関数（ライン数用、デフォルト値1）
    const padLineCountArray = (arr?: number[]): number[] => {
      if (!arr) arr = [];
      return arr.length < 12 ? [...arr, ...Array(12 - arr.length).fill(1)] : arr;
    };

    // 不足分を充填する関数（boolean）
    const padBooleanArray = (arr?: boolean[]): boolean[] => {
      if (!arr) arr = [];
      return arr.length < 12 ? [...arr, ...Array(12 - arr.length).fill(false)] : arr;
    };

    // 正しい年度配列を生成
    const correctFiscalYears = getFiscalYearsString(
      props.projectStartDate || dayjs().format("YYYY-MM-DD"),
      props.workingDays.length
    );

    // 各正しい年度について、DBデータとマッチング
    const updatedWorkingDays = correctFiscalYears.map((correctFiscalYear) => {
      // ※会計年度文字列は表示言語でフォーマットが変わるため、完全一致ではなく年数値で判定する
      const matchedData = props.workingDays.find(item => isSameFiscalYear(item.fiscalYear, correctFiscalYear));

      let workingDaysData: WorkingDaysType;

      if (matchedData) {
        const updatedWorkingDaysData: WorkingDaysType = { ...matchedData.workingDaysData };
        updatedWorkingDaysData.factoryWorkingDays = padNumberArray(matchedData.workingDaysData.factoryWorkingDays);
        updatedWorkingDaysData.sumFactoryWorkingDays = updatedWorkingDaysData.factoryWorkingDays.reduce((acc, val) => acc + val, 0);
        updatedWorkingDaysData.workingDays = padNumberArray(matchedData.workingDaysData.workingDays);
        updatedWorkingDaysData.workOnWeekendAndHolidays = padNumberArray(matchedData.workingDaysData.workOnWeekendAndHolidays);
        updatedWorkingDaysData.workOnHolidays = padNumberArray(matchedData.workingDaysData.workOnHolidays);
        updatedWorkingDaysData.lineCount = padLineCountArray(matchedData.workingDaysData.lineCount);
        updatedWorkingDaysData.requiredOverTimes = padBooleanArray(matchedData.workingDaysData.requiredOverTimes);
        workingDaysData = updatedWorkingDaysData;
      } else {
        workingDaysData = createInitialWorkingDaysData();
      }

      return {
        fiscalYear: correctFiscalYear,
        workingDaysData: workingDaysData,
      };
    });

    // 初期状態での検証状態を反映
    const isValid = updatedWorkingDays.every(({ workingDaysData }) => validateWorkingDaysData(workingDaysData));

    // 親を更新（保存用）
    props.setIsValidWorkingDays(isValid);
    props.setWorkingDays(updatedWorkingDays);
  }, []);

  /**
   * 稼働日数データのバリデーション（状態を変更しない純粋関数）
   */
  const validateWorkingDaysData = useCallback((workingDaysData: WorkingDaysType): boolean => {
    const factoryWorkingDays = workingDaysData.factoryWorkingDays || Array(12).fill(0);
    for (let index = 0; index < 12; index++) {
      const sumDays = workingDaysData.workingDays[index] + workingDaysData.workOnWeekendAndHolidays[index];
      if (MONTHS[index] < sumDays) {
        return false;
      }
      if (workingDaysData.workOnWeekendAndHolidays[index] < workingDaysData.workOnHolidays[index]) {
        return false;
      }
      // 稼働日数 <= 工場稼働日数
      if (factoryWorkingDays[index] > 0 && workingDaysData.workingDays[index] > factoryWorkingDays[index]) {
        return false;
      }
    }
    return true;
  }, []);

  /**
   * 入力チェック（blur時）
   */
  const handleNumberInputBlur = useCallback(() => {
    const result = props.workingDays.every(({ workingDaysData }) => validateWorkingDaysData(workingDaysData));
    props.setIsValidWorkingDays(result);

    if (!result) {
      toast({
        title: TITLE,
        description: t(`${WD}.toast.inputError`),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
    }
  }, [props.workingDays, props.setIsValidWorkingDays, validateWorkingDaysData, toast]);

  /**
   * 稼働日数変更ハンドラ（メモ化）
   */
  const handleWorkingDaysChange = useCallback((
    fiscalYear: string,
    propertyName: 'factoryWorkingDays' | 'workingDays' | 'workOnWeekendAndHolidays' | 'workOnHolidays',
    index: number,
    value: number
  ) => {
    const sumPropertyMap: Record<string, 'sumFactoryWorkingDays' | 'sumWorkingDays' | 'sumWeekendWorkDays' | 'sumHolidayWorkDays'> = {
      factoryWorkingDays: 'sumFactoryWorkingDays',
      workingDays: 'sumWorkingDays',
      workOnWeekendAndHolidays: 'sumWeekendWorkDays',
      workOnHolidays: 'sumHolidayWorkDays',
    };
    const sumPropertyName = sumPropertyMap[propertyName];

    props.setWorkingDays((prevState) => {
      if (!prevState) return prevState;

      const targetIndex = prevState.findIndex(item => item.fiscalYear === fiscalYear);
      if (targetIndex < 0) return prevState;

      const targetWorkingDaysData = prevState[targetIndex].workingDaysData;
      try {
        const updatedArray = [...targetWorkingDaysData[propertyName]];
        updatedArray[index] = value;

        const sum = updatedArray.reduce((acc, val) => acc + val, 0);

        const updatedWorkingDaysData = {
          ...targetWorkingDaysData,
          [propertyName]: updatedArray,
          [sumPropertyName]: sum,
        };

        const updatedState = [...prevState];
        updatedState[targetIndex] = {
          ...prevState[targetIndex],
          workingDaysData: updatedWorkingDaysData,
        };

        return updatedState;
      } catch (e) {
        console.error(e);
        console.error(`targetWorkingDaysData`, targetWorkingDaysData);
        return [];
      }
    });
  }, [props.setWorkingDays]);

  /**
   * 一括入力モーダルのライン数変更ハンドラ（メモ化）
   */
  const handleBulkLineCountChange = useCallback((index: number, value: number) => {
    setBulkMonthlyLineCount(prev => {
      const newData = [...prev];
      newData[index] = normalizeLineCount(value);
      return newData;
    });
  }, []);

  /**
   * ライン数変更ハンドラ（メモ化）
   */
  const handleLineCountChange = useCallback((fiscalYear: string, index: number, value: number) => {
    props.setWorkingDays((prevState) => {
      if (!prevState) return prevState;

      const targetIndex = prevState.findIndex((item) => item.fiscalYear === fiscalYear);
      if (targetIndex === -1) return prevState;

      const updatedWorkingDaysData = {
        ...prevState[targetIndex].workingDaysData,
      };

      const updatedLineCount = [...(updatedWorkingDaysData.lineCount || Array(12).fill(1))];
      updatedLineCount[index] = value;
      updatedWorkingDaysData.lineCount = updatedLineCount;

      const updatedState = [...prevState];
      updatedState[targetIndex] = {
        ...prevState[targetIndex],
        workingDaysData: updatedWorkingDaysData,
      };

      return updatedState;
    });
  }, [props.setWorkingDays]);

  /**
   * プロパティ変更ハンドラ（メモ化）
   */
  const handlePropertyChange = useCallback((fiscalYear: string, propertyName: string, newValue: any) => {
    props.setWorkingDays((prevState) => {
      if (!prevState) return prevState;

      const targetIndex = prevState.findIndex(item => item.fiscalYear === fiscalYear);
      if (targetIndex < 0) return prevState;

      const targetWorkingDaysData = {
        ...prevState[targetIndex].workingDaysData,
        [propertyName]: newValue,
      };

      const updatedState = [...prevState];
      updatedState[targetIndex] = {
        ...prevState[targetIndex],
        workingDaysData: targetWorkingDaysData,
      };

      return updatedState;
    });
  }, [props.setWorkingDays]);

  /**
   * 一括入力リセットボタン押下
   */
  const handleBulkReset = useCallback(() => {
    setBulkMonthlyFactoryWorkingDays(Array(12).fill(0));
    setBulkMonthlyWorkingDays(Array(12).fill(0));
    setBulkMonthlyWeekendWorkDays(Array(12).fill(0));
    setBulkMonthlyHolidayWorkDays(Array(12).fill(0));
    setBulkMonthlyLineCount(Array(12).fill(1));

    toast({
      title: t(`${WD}.toast.resetComplete`),
      description: t(`${WD}.toast.resetCompleteDesc`),
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  }, [toast]);

  /**
   * 一括入力ボタン押下
   */
  const handleBulkUpdate = useCallback(() => {
    // 開始年度と終了年度のインデックスを取得
    const startIndex = props.workingDays.findIndex(item => item.fiscalYear === bulkStartFiscalYear);
    const endIndex = props.workingDays.findIndex(item => item.fiscalYear === bulkEndFiscalYear);

    // 必須チェック
    if (startIndex === -1 || endIndex === -1) {
      toast({
        title: t(`${WD}.toast.error`),
        description: t(`${WD}.toast.fiscalYearNotFound`),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (startIndex > endIndex) {
      toast({
        title: t(`${WD}.toast.error`),
        description: t(`${WD}.toast.startBeforeEnd`),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 一括入力値の事前検証

    for (let bulkIndex = 0; bulkIndex < 12; bulkIndex++) {
      const monthsIndex = (bulkIndex + 3) % 12;
      const maxDays = MONTHS[monthsIndex];
      const factoryWorkingDay = bulkMonthlyFactoryWorkingDays[bulkIndex] || 0;
      const workingDay = bulkMonthlyWorkingDays[bulkIndex] || 0;
      const weekendWorkDay = bulkMonthlyWeekendWorkDays[bulkIndex] || 0;
      const holidayWorkDay = bulkMonthlyHolidayWorkDays[bulkIndex] || 0;

      if (workingDay + weekendWorkDay > maxDays) {
        toast({
          title: t(`${WD}.toast.inputErrorTitle`),
          description: t(`${WD}.toast.workingDaysExceedMax`, { month: t('common.format.month', { month: monthsIndex + 1 }), days: maxDays }),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (factoryWorkingDay > 0 && workingDay > factoryWorkingDay) {
        toast({
          title: t(`${WD}.toast.inputErrorTitle`),
          description: t(`${WD}.toast.workingDaysExceedFactory`, { month: t('common.format.month', { month: monthsIndex + 1 }), days: factoryWorkingDay }),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (holidayWorkDay > weekendWorkDay) {
        toast({
          title: t(`${WD}.toast.inputErrorTitle`),
          description: t(`${WD}.toast.holidayWorkExceedWeekend`, { month: t('common.format.month', { month: monthsIndex + 1 }), days: weekendWorkDay }),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    // 状態を更新
    props.setWorkingDays((prevState) => {
      const updatedState = prevState.map((item, index) => {
        if (index < startIndex || index > endIndex) {
          return item;
        }

        // 変換: 4月～3月 → 1月～12月
        const updatedFactoryWorkingDays = [
          ...bulkMonthlyFactoryWorkingDays.slice(9, 12),
          ...bulkMonthlyFactoryWorkingDays.slice(0, 9),
        ];

        const updatedWorkingDays = [
          ...bulkMonthlyWorkingDays.slice(9, 12),
          ...bulkMonthlyWorkingDays.slice(0, 9),
        ];

        const updatedWeekendWorkDays = [
          ...bulkMonthlyWeekendWorkDays.slice(9, 12),
          ...bulkMonthlyWeekendWorkDays.slice(0, 9),
        ];

        const updatedHolidayWorkDays = [
          ...bulkMonthlyHolidayWorkDays.slice(9, 12),
          ...bulkMonthlyHolidayWorkDays.slice(0, 9),
        ];

        const updatedLineCount = [
          ...bulkMonthlyLineCount.slice(9, 12),
          ...bulkMonthlyLineCount.slice(0, 9),
        ];

        const updatedWorkingDaysData = {
          ...item.workingDaysData,
          factoryWorkingDays: updatedFactoryWorkingDays,
          workingDays: updatedWorkingDays,
          workOnWeekendAndHolidays: updatedWeekendWorkDays,
          workOnHolidays: updatedHolidayWorkDays,
          lineCount: updatedLineCount,
          sumFactoryWorkingDays: updatedFactoryWorkingDays.reduce((sum, value) => sum + value, 0),
          sumWorkingDays: updatedWorkingDays.reduce((sum, value) => sum + value, 0),
          sumWeekendWorkDays: updatedWeekendWorkDays.reduce((sum, value) => sum + value, 0),
          sumHolidayWorkDays: updatedHolidayWorkDays.reduce((sum, value) => sum + value, 0),
        };

        return { ...item, workingDaysData: updatedWorkingDaysData };
      });

      // 検証
      const isValid = updatedState.every(({ workingDaysData }) => validateWorkingDaysData(workingDaysData));
      props.setIsValidWorkingDays(isValid);

      return updatedState;
    });

    toast({
      title: t(`${WD}.toast.bulkInputComplete`),
      description: t(`${WD}.toast.bulkInputCompleteDesc`, { startFY: bulkStartFiscalYear, endFY: bulkEndFiscalYear }),
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  }, [
    props.workingDays,
    props.setWorkingDays,
    props.setIsValidWorkingDays,
    bulkStartFiscalYear,
    bulkEndFiscalYear,
    bulkMonthlyFactoryWorkingDays,
    bulkMonthlyWorkingDays,
    bulkMonthlyWeekendWorkDays,
    bulkMonthlyHolidayWorkDays,
    bulkMonthlyLineCount,
    validateWorkingDaysData,
    toast,
  ]);

  /**
   * レイアウト
   */
  return (
    <>

      <Box className="simulation-working-days-container" maxHeight={props.maxHeight} overflowY="auto">

        <Stack spacing={0}>

          {/* 一括入力アコーディオン */}
          <Accordion allowToggle defaultIndex={[0]}>
            <AccordionItem>
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    {t(`${WD}.bulkInput`)}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <Stack spacing={4}>

                  {/* 年度範囲選択 */}
                  <HStack spacing={4} align="start" mb={4}>
                    <FormControl ml={5} w={"fit-content"}>
                      <Flex align="center">
                        <Text minW={75} m={0} whiteSpace="nowrap">{t(`${WD}.startFiscalYear`)}</Text>
                        <Select
                          width="150px"
                          value={bulkStartFiscalYear}
                          onChange={(e) => setBulkStartFiscalYear(e.target.value)}
                        >
                          {fiscalYearsString?.map((fiscalYear) => (
                            <option key={fiscalYear} value={fiscalYear}>
                              {fiscalYear}
                            </option>
                          ))}
                        </Select>
                      </Flex>
                    </FormControl>

                    <FormControl ml={2} w={"fit-content"}>
                      <Flex align="center">
                        <Text minW={75} m={0} whiteSpace="nowrap">{t(`${WD}.endFiscalYear`)}</Text>
                        <Select
                          width="150px"
                          value={bulkEndFiscalYear}
                          onChange={(e) => setBulkEndFiscalYear(e.target.value)}
                        >
                          {fiscalYearsString?.map((fiscalYear) => (
                            <option key={fiscalYear} value={fiscalYear}>
                              {fiscalYear}
                            </option>
                          ))}
                        </Select>
                      </Flex>
                    </FormControl>
                  </HStack>

                  {/* 月別入力テーブル */}
                  <Table>
                    <Thead>
                      <Tr>
                        <Td></Td>
                        {FISCAL_YEAR_MONTHS.map((maxDays, idx) => {
                          const displayMonth = idx < 9 ? idx + 4 : idx - 8; // 4,5,6...12,1,2,3
                          return (
                            <Td key={idx} padding={1} textAlign="center" fontSize={16}>
                              <Tooltip label={t(`${WD}.tooltip.daysInMonth`, { days: maxDays })}>
                                {t('common.format.month', { month: displayMonth })}
                              </Tooltip>
                            </Td>
                          );
                        })}
                        <Td whiteSpace="nowrap" textAlign="center" fontSize={16}>
                          {t(`${WD}.yearlyTotal`)}
                        </Td>
                      </Tr>
                    </Thead>

                    <Tbody>
                      {/* 工場稼働日数 */}
                      <Tr>
                        <Td>
                          <Tooltip placement="top" hasArrow label={t(`${WD}.tooltip.calendarWorkingDays`)}>
                            {t(`${WD}.factoryWorkingDays`)}
                          </Tooltip>
                        </Td>
                        {FISCAL_YEAR_MONTHS.map((maxDays, idx) => (
                          <Td key={idx} padding={1}>
                            <NumberInput
                              w="70px"
                              min={0}
                              max={maxDays}
                              precision={0}
                              step={1}
                              clampValueOnBlur={true}
                              value={bulkMonthlyFactoryWorkingDays[idx]}
                              onChange={(_, valueAsNumber) => {
                                if (isNaN(valueAsNumber)) valueAsNumber = 0;
                                if (valueAsNumber < 0) valueAsNumber = 0;
                                if (valueAsNumber > maxDays) valueAsNumber = maxDays;
                                const newData = [...bulkMonthlyFactoryWorkingDays];
                                newData[idx] = valueAsNumber;
                                setBulkMonthlyFactoryWorkingDays(newData);
                              }}
                            >
                              <NumberInputField textAlign="right" />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                        ))}
                        <Td minW={100} textAlign="right">
                          <Text>{bulkMonthlyFactoryWorkingDays.reduce((sum, value) => sum + value, 0)} {t('common.label.days')}</Text>
                        </Td>
                      </Tr>

                      {/* (内)稼働日数 */}
                      <Tr>
                        <Td>
                          {t(`${WD}.innerWorkingDays`)}
                        </Td>
                        {FISCAL_YEAR_MONTHS.map((maxDays, idx) => (
                          <Td key={idx} padding={1}>
                            <NumberInput
                              w="70px"
                              min={0}
                              max={maxDays}
                              precision={0}
                              step={1}
                              clampValueOnBlur={true}
                              value={bulkMonthlyWorkingDays[idx]}
                              onChange={(_, valueAsNumber) => {
                                if (isNaN(valueAsNumber)) valueAsNumber = 0;
                                if (valueAsNumber < 0) valueAsNumber = 0;
                                if (valueAsNumber > maxDays) valueAsNumber = maxDays;
                                const newData = [...bulkMonthlyWorkingDays];
                                newData[idx] = valueAsNumber;
                                setBulkMonthlyWorkingDays(newData);
                              }}
                            >
                              <NumberInputField textAlign="right" />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                        ))}
                        <Td minW={100} textAlign="right">
                          <Text>{bulkMonthlyWorkingDays.reduce((sum, value) => sum + value, 0)} {t('common.label.days')}</Text>
                        </Td>
                      </Tr>

                      {props.showWorkOnHoliday && (
                        <>
                          {/* 休日稼働日数 */}
                          <Tr>
                            <Td>
                              {t(`${WD}.holidayWorkDays`)}
                            </Td>
                            {Array.from({ length: 12 }, (_, idx) => (
                              <Td key={idx} padding={1}>
                                <NumberInput
                                  w="70px"
                                  min={0}
                                  max={31}
                                  precision={0}
                                  step={1}
                                  clampValueOnBlur={true}
                                  value={bulkMonthlyWeekendWorkDays[idx]}
                                  onChange={(_, valueAsNumber) => {
                                    if (isNaN(valueAsNumber)) valueAsNumber = 0;
                                    if (valueAsNumber < 0) valueAsNumber = 0;
                                    const newData = [...bulkMonthlyWeekendWorkDays];
                                    newData[idx] = valueAsNumber;
                                    setBulkMonthlyWeekendWorkDays(newData);
                                  }}
                                >
                                  <NumberInputField textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                              </Td>
                            ))}
                            <Td textAlign="right">
                              <Text>{bulkMonthlyWeekendWorkDays.reduce((sum, value) => sum + value, 0)} {t('common.label.days')}</Text>
                            </Td>
                          </Tr>

                          {/* (内)祝日出勤 */}
                          <Tr>
                            <Td>
                              <Tooltip placement="top" hasArrow label={<Box whiteSpace="pre-wrap">{t(`${WD}.tooltip.holidayWorkNote`)}</Box>}>
                                {t(`${WD}.holidayWork`)}
                              </Tooltip>
                            </Td>
                            {Array.from({ length: 12 }, (_, idx) => (
                              <Td key={idx} padding={1}>
                                <NumberInput
                                  w="70px"
                                  min={0}
                                  max={31}
                                  precision={0}
                                  step={1}
                                  clampValueOnBlur={true}
                                  value={bulkMonthlyHolidayWorkDays[idx]}
                                  onChange={(_, valueAsNumber) => {
                                    if (isNaN(valueAsNumber)) valueAsNumber = 0;
                                    if (valueAsNumber < 0) valueAsNumber = 0;
                                    const newData = [...bulkMonthlyHolidayWorkDays];
                                    newData[idx] = valueAsNumber;
                                    setBulkMonthlyHolidayWorkDays(newData);
                                  }}
                                >
                                  <NumberInputField textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                              </Td>
                            ))}
                            <Td textAlign="right">
                              <Text>{bulkMonthlyHolidayWorkDays.reduce((sum, value) => sum + value, 0)} {t('common.label.days')}</Text>
                            </Td>
                          </Tr>
                        </>
                      )}

                      {/* 稼働ライン数 */}
                      <Tr>
                        <Td>
                          <Tooltip placement="top" hasArrow label={<Box whiteSpace="pre-wrap">{t(`${WD}.tooltip.activeLinesNote`)}</Box>}>
                            {t(`${WD}.activeLines`)}
                          </Tooltip>
                        </Td>
                        {Array.from({ length: 12 }, (_, idx) => (
                          <MemoizedLineCountCell
                            key={idx}
                            value={bulkMonthlyLineCount[idx]}
                            shiftedIndex={idx}
                            onChange={handleBulkLineCountChange}
                          />
                        ))}
                        <Td textAlign="right">
                          <Text></Text>
                        </Td>
                      </Tr>

                      <Tr>
                        <Td colSpan={13} textAlign="right">
                          <Text w="fit-content" ml="auto">{t(`${WD}.totalActualWorkingDays`)}</Text>
                        </Td>
                        <Td textAlign="right">
                          {/* 実稼働日数合計 */}
                          <Text>{
                            bulkMonthlyWorkingDays.reduce((sum, value) => sum + value, 0)
                            + (props.showWorkOnHoliday ? bulkMonthlyWeekendWorkDays.reduce((sum, value) => sum + value, 0) : 0)
                          } {t('common.label.days')}</Text>
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>

                  {/* 一括適用ボタン */}
                  <Box textAlign={"right"} mt={2}>
                    <Button
                      m={1}
                      colorScheme="gray"
                      onClick={() => handleBulkReset()}
                    >
                      {t(`${WD}.reset`)}
                    </Button>
                    <Button
                      m={1}
                      colorScheme="blue"
                      onClick={() => handleBulkUpdate()}
                    >
                      {t(`${WD}.bulkApply`)}
                    </Button>
                  </Box>

                </Stack>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          {/* 稼働日数アコーディオン（年度別） */}
          {props.workingDays.map((workingDaysItem, index) => (
            <FiscalYearWorkingDays
              key={workingDaysItem.fiscalYear}
              fiscalYear={workingDaysItem.fiscalYear}
              fiscalYearLabel={fiscalYearsString[index]}
              workingDaysData={workingDaysItem.workingDaysData}
              showWorkOnHoliday={props.showWorkOnHoliday}
              onWorkingDaysChange={handleWorkingDaysChange}
              onLineCountChange={handleLineCountChange}
              onPropertyChange={handlePropertyChange}
              onBlur={handleNumberInputBlur}
            />
          ))}

        </Stack >

      </Box>

    </>
  );
});

export default WorkingDays;